import React from 'react';
import styled from 'styled-components';
import { Shield, Zap, Lock, ExternalLink } from 'lucide-react';

const TrustContainer = styled.div`
  width: 100%;
  margin-top: 2rem;
  padding: 1.5rem;
  background: ${({ theme }) => theme.colors.glass};
  border: 1px solid ${({ theme }) => theme.colors.glassBorder};
  border-radius: 20px;
  backdrop-filter: blur(10px);
`;

const TrustTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-align: center;
  margin-bottom: 1.25rem;
`;

const BadgesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const Badge = styled.a`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ theme }) => theme.colors.glassBorder};
  border-radius: 12px;
  text-decoration: none;
  text-align: left;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.glassHover};
    border-color: rgba(0, 208, 192, 0.3);
    transform: translateY(-2px);
  }
`;

const BadgeIcon = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.$color};
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
    color: white;
  }
`;

const BadgeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const BadgeTitle = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: 0.375rem;
  justify-content: flex-start;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.5;
  }
`;

const BadgeDescription = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 0.125rem;
  text-align: left;
`;

const VerifiedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #22c55e;
  text-transform: uppercase;
  margin-left: 0.5rem;
`;

export const TrustBadges: React.FC = () => {
  const badges = [
    {
      icon: <Zap />,
      color: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      title: 'Chainlink VRF',
      description: 'Verifiable random numbers',
      link: 'https://chain.link/vrf',
      verified: true,
    },
    {
      icon: <Shield />,
      color: 'linear-gradient(135deg, #00D0C0 0%, #059669 100%)',
      title: 'Smart Contract',
      description: 'On-chain & transparent',
      link: 'https://arbiscan.io/address/0x47a23F6650029A777d6411a01D52d2C64220bC87',
      verified: true,
    },
    {
      icon: <Lock />,
      color: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      title: 'Arbitrum Network',
      description: 'Fast & low-cost L2',
      link: 'https://arbitrum.io',
      verified: true,
    },
  ];

  return (
    <TrustContainer>
      <TrustTitle>Secured & Verified</TrustTitle>
      <BadgesGrid>
        {badges.map((badge) => (
          <Badge 
            key={badge.title} 
            href={badge.link} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <BadgeIcon $color={badge.color}>
              {badge.icon}
            </BadgeIcon>
            <BadgeContent>
              <BadgeTitle>
                {badge.title}
                {badge.verified && <VerifiedTag>Verified</VerifiedTag>}
                <ExternalLink />
              </BadgeTitle>
              <BadgeDescription>{badge.description}</BadgeDescription>
            </BadgeContent>
          </Badge>
        ))}
      </BadgesGrid>
    </TrustContainer>
  );
};

export default TrustBadges;
